import React, { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { TrainingAPI } from "@api/TrainingAPI";
import { useChatContext } from "../context";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { mapStoredMessages, groupConversationsByDate, formatConversationDate, CONVERSATION_PAGE_SIZE as PAGE_SIZE } from "../utils";
import type { ConversationSummary } from "../types";
import { PanelLeftClose, PanelLeftOpen, SquarePen } from "lucide-react";

const trainingAPI = new TrainingAPI();

interface ConversationSidebarProps {
    collapsed: boolean;
    onToggleCollapse: () => void;
    onUseClassicView?: () => void;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({ collapsed, onToggleCollapse, onUseClassicView }) => {
    const [loading, setLoading] = useState(false);
    const [loadingId, setLoadingId] = useState<number | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    // Set when a page fails. A new observer would fire at once while the sentinel is still in
    // view, so the list shows a Retry button instead of retrying by itself.
    const [pageFailed, setPageFailed] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const {
        conversations,
        setConversations,
        conversationId,
        isTyping,
        setConversation,
        resetConversation,
    } = useChatContext();

    useEffect(() => {
        setLoading(true);
        trainingAPI.getChatbotV2Conversations({ limit: PAGE_SIZE })
            .then((page: ConversationSummary[]) => {
                setConversations(page);
                setHasMore(page.length === PAGE_SIZE);
            })
            .catch(() => message.error("Could not load conversations."))
            .finally(() => setLoading(false));
    }, []);

    // Older pages load when the sentinel under the list scrolls into view. The backend sorts by
    // creation, newest first, and new conversations join the top of this list too, so the list
    // is always a prefix of the server's and its length is the next offset. A conversation made
    // in another tab shifts the server list down by one, and the id filter drops the row that
    // comes back twice. A conversation deleted elsewhere shifts it up, and one row is skipped
    // until a reload (nothing in this UI deletes). The observer is rebuilt after each page.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore || loading || loadingMore || pageFailed) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            setLoadingMore(true);
            trainingAPI.getChatbotV2Conversations({ limit: PAGE_SIZE, offset: conversations.length })
                .then((page: ConversationSummary[]) => {
                    setConversations(prev => {
                        const known = new Set(prev.map(c => c.id));
                        return [...prev, ...page.filter(c => !known.has(c.id))];
                    });
                    setHasMore(page.length === PAGE_SIZE);
                })
                .catch(() => {
                    message.error("Could not load more conversations.");
                    setPageFailed(true);
                })
                .finally(() => setLoadingMore(false));
        }, { root: listRef.current, rootMargin: "200px" });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loading, loadingMore, pageFailed, conversations.length, collapsed]);

    const onSelectConversation = (id: number) => {
        if (loadingId !== null || isTyping || id === conversationId) return;
        setLoadingId(id);
        trainingAPI.getChatbotV2Conversation(id)
            .then((data: any) => {
                if (!data?.id) throw new Error("Invalid conversation data");
                const { messages, stepsByTurn, openQuestionIds, problems } = mapStoredMessages(data.messages ?? []);
                setConversation({ id: data.id, messages, stepsByTurn, openQuestionIds });
                // An unknown part is a later backend's; a malformed part or open list is a bug.
                for (const problem of problems) {
                    if (problem.kind === "unknown") console.warn("[Chatbot] unknown stored part", problem.part);
                    else Sentry.captureException(new Error(`[Chatbot] stored ${problem.kind} agent data in conversation ${data.id}`), { extra: { part: problem.part } });
                }
            })
            .catch(() => message.error("Could not load conversation."))
            .finally(() => setLoadingId(null));
    };

    const groups = groupConversationsByDate(conversations);

    return (
        <aside className={`chat-v2-sidebar${collapsed ? " collapsed" : ""}`}>
            <div className="chat-v2-sidebar-header">
                <button
                    className="chat-v2-sidebar-icon-btn"
                    onClick={onToggleCollapse}
                    aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                    {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                </button>
                {!collapsed && (
                    <button
                        className="chat-v2-sidebar-new-btn"
                        onClick={resetConversation}
                        aria-label="New chat"
                    >
                        <SquarePen size={15} />
                        New Chat
                    </button>
                )}
            </div>

            {!collapsed && (
                <div className="chat-v2-sidebar-list" ref={listRef}>
                    {loading && (
                        <div className="chat-v2-sidebar-loading">Loading…</div>
                    )}

                    {!loading && conversations.length === 0 && (
                        <div className="chat-v2-sidebar-empty">No conversations yet</div>
                    )}

                    {groups.map(group => (
                        <div key={group.label}>
                            <div className="chat-v2-sidebar-group-label">{group.label}</div>
                            {group.items.map(conv => (
                                <button
                                    key={conv.id}
                                    className={`chat-v2-sidebar-item${conv.id === conversationId ? " active" : ""}${loadingId === conv.id ? " loading" : ""}`}
                                    onClick={() => onSelectConversation(conv.id)}
                                    disabled={loadingId !== null || isTyping}
                                >
                                    {conv.title
                                        ? conv.title.length > 40
                                            ? conv.title.slice(0, 39).trimEnd() + "…"
                                            : conv.title
                                        : "New conversation"}
                                </button>
                            ))}
                        </div>
                    ))}

                    {loadingMore && (
                        <div className="chat-v2-sidebar-loading">Loading…</div>
                    )}
                    {pageFailed && (
                        <button type="button" className="chat-v2-sidebar-retry" onClick={() => setPageFailed(false)}>
                            Could not load older chats. Retry
                        </button>
                    )}
                    {hasMore && <div ref={sentinelRef} aria-hidden="true" />}
                </div>
            )}

            {!collapsed && onUseClassicView && (
                <div className="chat-v2-sidebar-footer">
                    <button className="chat-v2-sidebar-classic-btn" onClick={onUseClassicView}>
                        Switch to classic view
                    </button>
                </div>
            )}
        </aside>
    );
};

export default ConversationSidebar;
